import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { ReactComponent as PlusIcon } from "../assets/icons/plus.svg";
import AddEditSpecialPartnerModal from "../components/SpecialPartners/modals/AddEditSpecialPartnerModal";
import DeleteSpecialPartnerModal from "../components/SpecialPartners/modals/DeleteSpecialPartnerModal";
import UploadReportModal from "../components/SpecialPartners/modals/UploadReportModal";
import {
  listSpecialPartners,
  listAllCampaignTransactions,
  listOfflineMerchants,
  SpecialPartnerViewModel,
  AllCampaignTransactionViewModel,
  OfflineMerchantViewModel,
} from "../api/specialPartners";

const AdminSpecialPartners: React.FC = () => {
  const [partners, setPartners] = useState<SpecialPartnerViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<SpecialPartnerViewModel | null>(null);

  const [merchants, setMerchants] = useState<OfflineMerchantViewModel[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<string | null>(null);
  const [txRows, setTxRows] = useState<AllCampaignTransactionViewModel[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(0);
  const [txLoading, setTxLoading] = useState(false);
  const TX_PAGE_SIZE = 20;

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const [data, merchantData] = await Promise.all([
        listSpecialPartners(),
        listOfflineMerchants(),
      ]);
      setPartners(data);
      setMerchants(merchantData);
      setError(null);
      fetchAllTransactions(0, null);
    } catch (err) {
      console.error("Error fetching special partners:", err);
      setError("Failed to load special partners. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTransactions = async (page: number, merchant: string | null) => {
    setTxLoading(true);
    try {
      const res = await listAllCampaignTransactions(page, TX_PAGE_SIZE, merchant ?? undefined);
      setTxRows(res.data);
      setTxTotal(res.total);
      setTxPage(res.pageNumber);
    } catch (err) {
      console.error("Error fetching all transactions:", err);
    } finally {
      setTxLoading(false);
    }
  };

  const handleOpenEdit = (partner: SpecialPartnerViewModel) => {
    setSelectedPartner(partner);
    setIsAddEditModalOpen(true);
  };

  const handleOpenDelete = (partner: SpecialPartnerViewModel) => {
    setSelectedPartner(partner);
    setIsDeleteModalOpen(true);
  };


  const handleSaveSuccess = (partnerName: string, isEdit: boolean) => {
    fetchPartners();
    setSuccessMessage(
      isEdit
        ? `${partnerName} updated successfully.`
        : `${partnerName} added successfully.`
    );
    setIsAddEditModalOpen(false);
    setSelectedPartner(null);
  };

  const handleTxPageChange = (page: number) => {
    fetchAllTransactions(page, selectedMerchant);
  };

  const handleMerchantChange = (merchant: string | null) => {
    setSelectedMerchant(merchant);
    fetchAllTransactions(0, merchant);
  };

  const handleDeleteSuccess = (partnerName: string) => {
    fetchPartners();
    setSuccessMessage(`${partnerName} removed successfully.`);
    setIsDeleteModalOpen(false);
    setSelectedPartner(null);
  };

  const formatCashback = (
    partner: SpecialPartnerViewModel,
    field: "totalCashback" | "userCashback"
  ) => {
    const value = partner[field];
    const formatted = Number(value).toFixed(2);
    return partner.cashbackType === "Percentage"
      ? `${formatted}%`
      : `${formatted} DKK`;
  };

  if (loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <div className="flex max-md:flex-col md:items-center md:justify-between mb-10 max-md:gap-4">
        <h2 className="font-medium">Offline Campaigns</h2>
        <div className="flex gap-3">
          <Button
            onClick={() => setIsUploadModalOpen(true)}
            variant="outline"
            disabled={partners.length === 0}
          >
            Upload Report
          </Button>
          <Button
            onClick={() => {
              setSelectedPartner(null);
              setIsAddEditModalOpen(true);
            }}
            variant="light"
            className="gap-2"
          >
            Add Campaign
            <PlusIcon />
          </Button>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
            ×
          </button>
        </div>
      )}

      {partners.length === 0 ? (
        <div className="text-center text-gray-500 my-8">
          No campaigns found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table-default">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Cashback Type</th>
                <th>Total Cashback</th>
                <th>User Cashback</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner.id}>
                  <td className="font-medium">{partner.campaignName}</td>
                  <td>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        partner.cashbackType === "Fixed"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-purple-100 text-purple-800"
                      }`}
                    >
                      {partner.cashbackType}
                    </span>
                  </td>
                  <td>{formatCashback(partner, "totalCashback")}</td>
                  <td>{formatCashback(partner, "userCashback")}</td>
                  <td className="text-right">
                    <div className="flex gap-2 justify-end">
<Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenEdit(partner)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDelete(partner)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-10">
          <div className="flex items-center gap-4 mb-4">
            <h3 className="font-medium text-lg">All Transactions</h3>
            <Select
              options={merchants.map((m) => ({ value: m.merchantName, label: m.merchantName }))}
              value={selectedMerchant ? { value: selectedMerchant, label: selectedMerchant } : null}
              onChange={(opt) => handleMerchantChange(opt?.value ?? null)}
              placeholder="Select merchant..."
              isClearable
              className="basic-select w-64"
              classNamePrefix="select"
            />
          </div>

          {txLoading ? (
            <div className="flex justify-center py-8"><Loader /></div>
          ) : txRows.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No transactions found.</p>
          ) : (
            <>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="table-default">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Campaign</th>
                      <th>Amount</th>
                      <th>Total Cashback</th>
                      <th>User Cashback</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txRows.map((tx) => (
                      <tr key={tx.id}>
                        <td>{tx.userId}</td>
                        <td>{tx.campaignName}</td>
                        <td>{tx.transactionAmount.toFixed(2)} DKK</td>
                        <td>{tx.totalAssignedCashback.toFixed(2)} DKK</td>
                        <td>{tx.customerAssignedCashback.toFixed(2)} DKK</td>
                        <td className="whitespace-nowrap">{(() => {
                          const s = tx.transactionDate.replace(/\D/g, "");
                          if (s.length === 12) {
                            const d = new Date(2000 + +s.slice(0,2), +s.slice(2,4)-1, +s.slice(4,6), +s.slice(6,8), +s.slice(8,10), +s.slice(10,12));
                            return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
                          }
                          const d = new Date(tx.transactionDate.replace(" ", "T"));
                          return isNaN(d.getTime()) ? tx.transactionDate : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
                        })()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {txTotal > TX_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                  <span>
                    Showing {txPage * TX_PAGE_SIZE + 1}–{Math.min((txPage + 1) * TX_PAGE_SIZE, txTotal)} of {txTotal}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={txPage === 0}
                      onClick={() => handleTxPageChange(txPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(txPage + 1) * TX_PAGE_SIZE >= txTotal}
                      onClick={() => handleTxPageChange(txPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      <AddEditSpecialPartnerModal
        isOpen={isAddEditModalOpen}
        partner={selectedPartner}
        onClose={() => {
          setIsAddEditModalOpen(false);
          setSelectedPartner(null);
        }}
        onSuccess={handleSaveSuccess}
      />

      <DeleteSpecialPartnerModal
        isOpen={isDeleteModalOpen}
        partner={selectedPartner}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPartner(null);
        }}
        onSuccess={handleDeleteSuccess}
      />

      <UploadReportModal
        isOpen={isUploadModalOpen}
        partners={partners}
        onClose={() => setIsUploadModalOpen(false)}
      />
    </div>
  );
};

export default AdminSpecialPartners;
